import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Plus, Edit2, Trash2, CheckCircle2, Loader2, X, Home, Briefcase, Hash } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  label: 'Home',
  full_name: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  pincode: '',
  country: 'India',
  is_default: false,
};

const LABEL_ICONS = { Home: Home, Work: Briefcase, Other: Hash };

export default function ShopAddressesPage() {
  const { user } = useOutletContext();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) fetchAddresses();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const authHeaders = () => {
    const token = localStorage.getItem('dharmbook_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/shop/addresses`, { headers: authHeaders() });
      setAddresses(Array.isArray(data) ? data : []);
    } catch {
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); };
  const openEdit = (addr) => { setForm({ ...EMPTY_FORM, ...addr }); setEditingId(addr.id); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.line1 || !form.city || !form.state || !form.pincode) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!/^[0-9]{6}$/.test(form.pincode)) {
      toast.error('PIN code must be 6 digits');
      return;
    }
    if (!/^[0-9]{10}$/.test(form.phone)) {
      toast.error('Phone must be 10 digits');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const { data } = await axios.put(`${API}/shop/addresses/${editingId}`, form, { headers: authHeaders() });
        setAddresses((prev) => prev.map((a) => (a.id === editingId ? data : a)));
        toast.success('Address updated');
      } else {
        const { data } = await axios.post(`${API}/shop/addresses`, form, { headers: authHeaders() });
        setAddresses((prev) => [...prev, data]);
        toast.success('Address added');
      }
      setShowForm(false);
      // Re-fetch to keep `is_default` consistent across the list
      fetchAddresses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await axios.delete(`${API}/shop/addresses/${id}`, { headers: authHeaders() });
      setAddresses((prev) => prev.filter((a) => a.id !== id));
      toast.success('Address deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSetDefault = async (addr) => {
    try {
      const updated = { ...addr, is_default: true };
      const { data } = await axios.put(`${API}/shop/addresses/${addr.id}`, updated, { headers: authHeaders() });
      setAddresses((prev) => prev.map((a) => ({ ...a, is_default: a.id === data.id })));
      toast.success('Default address updated');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  if (!user) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }} data-testid="addresses-not-logged-in" className="shop-page">
        <MapPin className="w-12 h-12 mx-auto text-orange-400 mb-3" />
        <h2 className="text-lg font-bold mb-1">Login to manage addresses</h2>
        <p className="text-sm text-gray-500 mb-4">Save addresses for faster checkout</p>
        <Link to="/auth" className="inline-block px-5 py-2.5 bg-[#FF7A00] text-white font-bold rounded-lg" data-testid="addresses-login-cta">
          Login
        </Link>
      </div>
    );
  }

  return (
    <div className="shop-page" data-testid="addresses-page" style={{ paddingBottom: 100 }}>
      <div className="shop-fk-section" style={{ marginTop: 8 }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="shop-fk-section-title">Saved Addresses</h1>
            <p className="shop-fk-section-subtitle">{addresses.length} saved</p>
          </div>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-1 bg-[#FF7A00] text-white text-xs font-bold px-3 py-2 rounded-lg"
            data-testid="address-add-btn"
          >
            <Plus className="w-4 h-4" /> Add New
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>
      ) : addresses.length === 0 ? (
        <div className="shop-fk-section" data-testid="addresses-empty">
          <div className="text-center py-8">
            <MapPin className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-gray-800 mb-1">No saved addresses yet</h3>
            <p className="text-sm text-gray-500 mb-5">Add an address for quick & easy checkout.</p>
            <button onClick={openAdd} className="inline-flex items-center gap-1 px-5 py-2.5 bg-[#FF7A00] text-white font-bold rounded-lg text-sm" data-testid="addresses-add-first">
              <Plus className="w-4 h-4" /> Add First Address
            </button>
          </div>
        </div>
      ) : (
        <div className="shop-fk-section">
          <div className="space-y-3">
            {addresses.map((addr) => {
              const Icon = LABEL_ICONS[addr.label] || Hash;
              return (
                <div
                  key={addr.id}
                  className={`p-3 rounded-lg border ${addr.is_default ? 'border-[#FF7A00] bg-orange-50/50' : 'border-gray-200 bg-white'}`}
                  data-testid={`address-card-${addr.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="text-sm font-bold text-gray-900">{addr.label}</span>
                        {addr.is_default && (
                          <span className="text-[10px] font-bold text-[#FF7A00] bg-orange-100 px-1.5 py-0.5 rounded">DEFAULT</span>
                        )}
                      </div>
                      <div className="text-sm font-semibold text-gray-800">{addr.full_name}</div>
                      <div className="text-xs text-gray-500 leading-relaxed mt-0.5">
                        {addr.line1}{addr.line2 ? `, ${addr.line2}` : ''}, {addr.city}, {addr.state} - {addr.pincode}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">📞 {addr.phone}</div>
                      <div className="flex items-center gap-3 mt-2">
                        {!addr.is_default && (
                          <button
                            onClick={() => handleSetDefault(addr)}
                            className="text-[11px] font-bold text-[#FF7A00]"
                            data-testid={`address-set-default-${addr.id}`}
                          >
                            <CheckCircle2 className="w-3 h-3 inline-block mr-0.5" />
                            Set as Default
                          </button>
                        )}
                        <button
                          onClick={() => openEdit(addr)}
                          className="text-[11px] font-bold text-blue-600"
                          data-testid={`address-edit-${addr.id}`}
                        >
                          <Edit2 className="w-3 h-3 inline-block mr-0.5" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(addr.id)}
                          className="text-[11px] font-bold text-red-500"
                          data-testid={`address-delete-${addr.id}`}
                        >
                          <Trash2 className="w-3 h-3 inline-block mr-0.5" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal form */}
      {showForm && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40"
          onClick={() => setShowForm(false)}
          data-testid="address-form-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto"
            data-testid="address-form-modal"
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
              <h2 className="text-base font-bold">{editingId ? 'Edit Address' : 'Add New Address'}</h2>
              <button onClick={() => setShowForm(false)} aria-label="Close" data-testid="address-form-close">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-4 space-y-3">
              {/* Label chips */}
              <div>
                <label className="text-xs text-gray-500 font-semibold mb-1.5 block">Save As</label>
                <div className="flex gap-2">
                  {['Home', 'Work', 'Other'].map((l) => (
                    <button
                      type="button"
                      key={l}
                      onClick={() => setForm((f) => ({ ...f, label: l }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border ${form.label === l ? 'bg-[#FF7A00] text-white border-[#FF7A00]' : 'bg-white text-gray-700 border-gray-200'}`}
                      data-testid={`address-label-${l}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 font-semibold mb-1 block">Full Name *</label>
                <input
                  type="text"
                  required
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  data-testid="address-form-name"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-semibold mb-1 block">10-digit Phone *</label>
                <input
                  type="tel"
                  required
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  data-testid="address-form-phone"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-semibold mb-1 block">Address Line 1 *</label>
                <input
                  type="text"
                  required
                  value={form.line1}
                  onChange={(e) => setForm({ ...form, line1: e.target.value })}
                  placeholder="House no., Street, Locality"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  data-testid="address-form-line1"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-semibold mb-1 block">Address Line 2</label>
                <input
                  type="text"
                  value={form.line2}
                  onChange={(e) => setForm({ ...form, line2: e.target.value })}
                  placeholder="Landmark, Apartment (optional)"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  data-testid="address-form-line2"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 font-semibold mb-1 block">City *</label>
                  <input
                    type="text"
                    required
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    data-testid="address-form-city"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-semibold mb-1 block">State *</label>
                  <input
                    type="text"
                    required
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    data-testid="address-form-state"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-semibold mb-1 block">PIN Code *</label>
                <input
                  type="tel"
                  required
                  inputMode="numeric"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  value={form.pincode}
                  onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '') })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  data-testid="address-form-pincode"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_default}
                  onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                  data-testid="address-form-default"
                />
                Set as default address
              </label>

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-[#FF7A00] text-white font-bold py-3 rounded-lg mt-2 disabled:opacity-60"
                data-testid="address-form-save"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (editingId ? 'Update Address' : 'Save Address')}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
